import {
  ClientError,
  ClientErrorType,
  InternalId,
  IRuntimeReference,
  IRuntimeRevisionReference,
  StaticRevisionReference,
} from '@edgebox/data-definition-kit';
import {
  MonitoringPromptPurposeType,
  ProjectPromptCoverageBlank,
  RECOMMENDER_ASSIGNABLE_PURPOSE_TYPES,
  SyncCoreDataDefinitionsEnumTranslator,
  TaxonomyFacet,
} from '@edgebox/sync-core-data-definitions';
import {
  ClientMonitoringPromptCoverageRequestDto,
  ClientMonitoringPromptRecommendationConfirmDto,
  ClientMonitoringPromptRecommendationDto,
  ClientMonitoringPromptRecommendationRequestDto,
  ClientProjectEntity,
  ClientRecommendationCoverageDto,
  ClientRecommendedMonitoringPromptDto,
  ClientTaxonomyTermEntity,
  ISyncCoreApi,
  MonitoringPromptRecommendationTier,
  OpenApi,
} from '@edgebox/sync-core-rest-client';
import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { sendToParent } from '../../../frame-messages';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import { ChipsDropdownSelect } from './Components/ChipsDropdownSelect';
import {
  EXTRACTION_TARGET_FACETS,
  OFFERED_EXTRACTION_TARGET_FACETS,
  formatFacetNameList,
  heldExtractedByDefaultFacets,
} from './Components/extractionFacets';
import {
  BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS,
  BRAND_PRESENCE_ONBOARDING_TOTAL_STEPS,
  brandPresenceOnboardingStepPath,
} from './brandPresencePaths';
import { taxonomyPath } from './GovernanceTaxonomyManagement.utils';
import { GovernanceOnboardingContainer, GovernanceOnboardingStepContainer } from './Onboarding/components/GovernanceOnboardingPrimitives';
import { GovernanceOnboardingListSkeleton } from './Onboarding/components/GovernanceOnboardingSkeleton';
import { TaxonomyTermIndexScope } from './Onboarding/ContentInventory/Components/TaxonomyTermIndexScope';
import { useTaxonomyTermIndex } from './Onboarding/ContentInventory/Components/taxonomyTermIndex';
import { TaxonomyTermNameBadge } from './Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';

/** Where this step sits in the wizard. */
const PROMPT_STEP_NUMBER = 6;

/**
 * Every vocabulary this step renders a tag name from, so the shared index fetches these and no more.
 *
 * A key outside the declared set resolves as missing and the raw key is what a reader would see, so
 * the list follows the recommender's own seating rules rather than the tags one example happens to
 * carry: the breadth grid seats a persona at a funnel stage, a comparison is templated over a rival
 * vocabulary, market criteria over markets, a sector question over a target industry and a brand
 * probe over the brand and its products. Beside those, the reports name the attributes, angles and
 * industries a set leaves uncovered, the topics it raises, the languages it is asked in, the engines
 * left out of the daily count, and the tags a prompt already has read out of its answers — including
 * one of a vocabulary no control offers, which still has to render and be removable.
 */
const RENDERED_TERM_FACETS: readonly TaxonomyFacet[] = [
  TaxonomyFacet.Persona,
  TaxonomyFacet.FunnelStage,
  TaxonomyFacet.Market,
  TaxonomyFacet.TargetIndustry,
  TaxonomyFacet.ContentAngle,
  TaxonomyFacet.AuthorityTopic,
  TaxonomyFacet.OrganizationAttribute,
  TaxonomyFacet.ProductAttribute,
  TaxonomyFacet.Locale,
  TaxonomyFacet.Engine,
  TaxonomyFacet.Program,
  TaxonomyFacet.CompetitorProgram,
  ...EXTRACTION_TARGET_FACETS,
];

/** The depths a customer can ask for, shallowest first. */
const TIERS_IN_ORDER: MonitoringPromptRecommendationTier[] = [
  MonitoringPromptRecommendationTier._100Starter,
  MonitoringPromptRecommendationTier._200Standard,
  MonitoringPromptRecommendationTier._300Full,
];

type TStepEnumName = 'MonitoringPromptPurposeType' | 'MonitoringPromptRecommendationTier' | 'TaxonomyFacet' | 'MonitoringPromptCountry';

function translateEnumValue(enumName: TStepEnumName, value: string): string {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue(enumName, value as any);
  } catch {
    return value;
  }
}

/** What the project read answers, replaced as a whole so the preview is asked for exactly once. */
interface IProjectContext {
  project: IRuntimeReference<ClientProjectEntity>;
  /** The project's own angle tags, which the set's angle coverage is measured against. */
  angles: IRuntimeRevisionReference<ClientTaxonomyTermEntity>[];
}

/** One rejected prompt of a confirm that wrote nothing, named by its place in the submitted list. */
type ConfirmRejection = OpenApi.ClientMonitoringPromptRecommendationConfirmErrorDto;

/**
 * The rejections a refused confirm named, read from the error the client threw.
 *
 * The client has already read the refusal's body: a 400 arrives as the kit's validation error, which
 * carries the list the body named as its details. Every other failure is a fault the caller reports
 * as one, and an entry that is not the documented pair is left out, so a malformed answer never
 * renders as a rejection.
 */
function confirmRejectionsOf(error: unknown): ConfirmRejection[] | undefined {
  if (!(error instanceof ClientError) || error.type !== ClientErrorType.Validation || !Array.isArray(error.details)) {
    return undefined;
  }

  return (error.details as unknown[])
    .filter((entry): entry is ConfirmRejection => {
      const record = entry as { promptIndex?: unknown; reason?: unknown };
      return typeof record?.promptIndex === 'number' && typeof record?.reason === 'string';
    })
    .map((entry) => ({ promptIndex: entry.promptIndex, reason: entry.reason }));
}

/** What the step says when the whole set was refused but no prompt was named. */
const SET_REFUSED_SENTENCE = 'Nothing was created. The set was refused; change the prompts and try again.';

/**
 * The sentence for a refused confirm that named no prompt: the server's own reason when it sent one,
 * and never the status label a refusal without a reason carries in its place. Undefined for anything
 * that is not a refusal, which the caller reports as the fault it is.
 */
function refusalSentenceOf(error: unknown): string | undefined {
  if (!(error instanceof ClientError) || error.type !== ClientErrorType.Validation) {
    return undefined;
  }

  return error.message && error.message !== error.errorLabel ? error.message : SET_REFUSED_SENTENCE;
}

/** A prompt of the working set with one property changed, keeping the class the client serializes by. */
function promptWith(
  prompt: ClientRecommendedMonitoringPromptDto,
  changes: Partial<ClientRecommendedMonitoringPromptDto>
): ClientRecommendedMonitoringPromptDto {
  return Object.assign(new ClientRecommendedMonitoringPromptDto(), prompt, changes);
}

/** The cell key a topic and a purpose kind share, so a blank and a count meet under one name. */
function matrixCellKey(topicKey: string, purposeType: MonitoringPromptPurposeType): string {
  return `${topicKey}::${purposeType}`;
}

function coverageLabel(coverage: ClientRecommendationCoverageDto | undefined): string {
  return coverage ? `${coverage.covered} of ${coverage.total}` : '0 of 0';
}

function CoverageRow({
  label,
  coverage,
  api,
}: {
  label: string;
  coverage: ClientRecommendationCoverageDto | undefined;
  api?: ISyncCoreApi;
}) {
  const missing = coverage?.missing ?? [];

  return (
    <div className="space-y-1">
      <div className="flex items-baseline gap-2">
        <span className="text-sm font-semibold text-slate-900">{label}</span>
        <span className="text-sm tabular-nums text-slate-500">{coverageLabel(coverage)}</span>
      </div>
      {missing.length ? (
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-xs text-slate-500">Not covered yet:</span>
          {missing.map((reference) => (
            <TaxonomyTermNameBadge key={reference.getStableKey()} api={api} termKey={reference.getStableKey()} applyFacetPresentation />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** The step, inside the tag-index scope it declares. The body follows. */
export function GovernanceOnboardingStepPrompts() {
  return (
    <TaxonomyTermIndexScope facets={RENDERED_TERM_FACETS}>
      <GovernanceOnboardingStepPromptsScreen />
    </TaxonomyTermIndexScope>
  );
}

function GovernanceOnboardingStepPromptsScreen() {
  const navigate = useNavigate();
  const apiContext = React.useContext(SyncCoreApiContext);
  const params = useParams<{ onboardingId?: string }>();
  const [searchParams] = useSearchParams();
  const taskGroupId = params.onboardingId || searchParams.get('taskGroupId') || undefined;

  const [tier, setTier] = React.useState<MonitoringPromptRecommendationTier>(MonitoringPromptRecommendationTier._200Standard);
  const [loading, setLoading] = React.useState(true);
  /** A set that could not be read. Asking again is the only thing to do with it. */
  const [loadError, setLoadError] = React.useState<string | undefined>(undefined);
  /** An action that did not land. Asking for the set again would throw away what is on screen. */
  const [actionError, setActionError] = React.useState<string | undefined>(undefined);
  const [recommendation, setRecommendation] = React.useState<ClientMonitoringPromptRecommendationDto | undefined>(undefined);
  const [prompts, setPrompts] = React.useState<ClientRecommendedMonitoringPromptDto[]>([]);
  /**
   * What the project read answered, as one value.
   *
   * The preview is asked for whenever this changes, so it is replaced only by a completed read and
   * never piecemeal: a half-updated pair would ask for the set twice, once against each half.
   */
  const [projectContext, setProjectContext] = React.useState<IProjectContext | undefined>(undefined);
  const [deliberateBlanks, setDeliberateBlanks] = React.useState<ProjectPromptCoverageBlank[]>([]);
  const [savingBlank, setSavingBlank] = React.useState(false);
  const [heldFacets, setHeldFacets] = React.useState<TaxonomyFacet[]>([]);
  const [tagOptions, setTagOptions] = React.useState<ClientTaxonomyTermEntity[] | undefined>(undefined);
  const [weightingConfirmed, setWeightingConfirmed] = React.useState(false);
  const [frequencyText, setFrequencyText] = React.useState<Record<number, string>>({});
  const [retry, setRetry] = React.useState(0);
  const [confirming, setConfirming] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState(false);
  const [rejections, setRejections] = React.useState<ConfirmRejection[] | undefined>(undefined);
  const [confirmWarnings, setConfirmWarnings] = React.useState<string[] | undefined>(undefined);

  const api = apiContext?.api;
  const { index: termIndex } = useTaxonomyTermIndex(api);

  // What the project itself says, which no depth changes: the project the set belongs to, the
  // coverage decisions already recorded on it, the vocabularies the account holds and the angles the
  // set is measured against. Read once, so asking for a different depth cannot revert a decision the
  // reader recorded a moment ago.
  React.useEffect(() => {
    if (!api || !taskGroupId) {
      setLoading(false);
      setLoadError('Missing onboarding context.');
      return;
    }

    let active = true;

    void (async () => {
      // Reported as running and cleared of what a previous attempt said, so asking again visibly
      // does something even when it fails with the very same words.
      setLoading(true);
      setLoadError(undefined);

      try {
        const taskGroup = await api.service.taskGroups.item(taskGroupId as InternalId, true);
        const project = taskGroup.project;
        if (!project) {
          throw new Error('Missing onboarding project reference.');
        }

        const projectId = project.getId();
        const [projectEntity, taxonomies, angleTerms] = await Promise.all([
          project.get(),
          api.service.taxonomies.list({ page: 0, itemsPerPage: 500 }, undefined),
          api.service.taxonomyTerms.list({ page: 0, itemsPerPage: 500 }, { projectId, taxonomyFacet: TaxonomyFacet.ContentAngle }),
        ]);

        if (!active) {
          return;
        }

        setDeliberateBlanks(projectEntity?.promptCoverage?.deliberateBlanks ?? []);
        setHeldFacets((taxonomies.items || []).map((taxonomy) => taxonomy.facet).filter((facet): facet is TaxonomyFacet => !!facet));
        setProjectContext({ project, angles: (angleTerms.items || []).map((term) => new StaticRevisionReference(term)) });
      } catch (projectError) {
        if (active) {
          setLoading(false);
          setLoadError((projectError as Error)?.message || 'Unable to read this project.');
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [api, retry, taskGroupId]);

  // The set itself, previewed for the chosen depth. A different depth is a different set, so the
  // working copy, the typed cadences, the weighting confirmation, any refusal from an earlier
  // attempt and the fact that a set was already created all start over — and a preview that fails
  // leaves no set behind, because a stale one would be what the create action submitted.
  React.useEffect(() => {
    if (!api || !projectContext) {
      return;
    }

    let active = true;

    void (async () => {
      setLoading(true);
      setLoadError(undefined);
      setActionError(undefined);
      setRejections(undefined);
      setConfirmWarnings(undefined);
      setWeightingConfirmed(false);
      setConfirmed(false);
      setFrequencyText({});

      try {
        const request = Object.assign(new ClientMonitoringPromptRecommendationRequestDto(), {
          project: projectContext.project,
          tier,
          // The wizard records no focus persona, so none is asked for; the weighting the recommender
          // applied comes back on the response and is confirmed below.
          angles: projectContext.angles,
        });

        const preview = await api.governance.monitoringPrompts.recommendationPreview(request);

        if (!active) {
          return;
        }

        setRecommendation(preview);
        setPrompts(preview.prompts || []);
      } catch (previewError) {
        if (!active) {
          return;
        }

        setRecommendation(undefined);
        setPrompts([]);
        setLoadError((previewError as Error)?.message || 'Unable to load the recommended prompts.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [api, projectContext, tier]);

  // The tags an extraction control works with arrive separately from the set, so the set renders
  // while they are still on their way. Until they land, `tagOptions` is undefined and every such
  // control is disabled — an adjustment made against a half-loaded list is what empties a previewed
  // value. Only the vocabularies a control OFFERS are asked for: a tag of one Sync Core reads out of
  // every answer is never offered, and one a prompt already carries renders from the shared index.
  React.useEffect(() => {
    if (!api) {
      return;
    }

    let active = true;

    void (async () => {
      try {
        const result = await api.service.taxonomyTerms.list(
          { page: 0, itemsPerPage: 500 },
          { taxonomyFacet: OFFERED_EXTRACTION_TARGET_FACETS }
        );

        if (active) {
          setTagOptions(result.items || []);
        }
      } catch {
        if (active) {
          setTagOptions([]);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [api]);

  const extractionHintNames = React.useMemo(
    () => heldExtractedByDefaultFacets(heldFacets).map((facet) => translateEnumValue('TaxonomyFacet', facet)),
    [heldFacets]
  );

  const blankCellKeys = React.useMemo(
    () => new Set(deliberateBlanks.map((blank) => matrixCellKey(blank.topic.getStableKey(), blank.purposeType))),
    [deliberateBlanks]
  );

  // The matrix counts the WORKING set, not the previewed one, so removing a prompt empties the cell
  // it filled and the gaps below stay honest about what is left.
  const countsByCell = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const prompt of prompts) {
      for (const topic of prompt.topics || []) {
        const key = matrixCellKey(topic.getStableKey(), prompt.purposeType);
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }
    return counts;
  }, [prompts]);

  const topicReferences = React.useMemo(() => {
    const byKey = new Map<string, IRuntimeRevisionReference<ClientTaxonomyTermEntity>>();
    for (const entry of recommendation?.topicDistribution || []) {
      byKey.set(entry.topic.getStableKey(), entry.topic);
    }
    for (const prompt of prompts) {
      for (const topic of prompt.topics || []) {
        byKey.set(topic.getStableKey(), topic);
      }
    }
    return Array.from(byKey.values());
  }, [prompts, recommendation?.topicDistribution]);

  // A prompt is asked in the countries and languages the project names, and the create action refuses
  // a set with a prompt asked nowhere and writes none of it. The recommender gives every prompt the
  // same pairs, so one prompt without any means the project has none to offer yet.
  const lacksCountryLanguage = React.useMemo(() => prompts.some((prompt) => !prompt.countryLocales?.length), [prompts]);

  const markDeliberateBlank = async (
    topic: IRuntimeRevisionReference<ClientTaxonomyTermEntity>,
    purposeType: MonitoringPromptPurposeType
  ) => {
    if (!api || !projectContext?.project) {
      return;
    }

    const blank = Object.assign(new ProjectPromptCoverageBlank(), { topic, purposeType });
    const next = [...deliberateBlanks, blank];

    setSavingBlank(true);
    try {
      await api.governance.monitoringPrompts.recommendationPromptCoverage(
        Object.assign(new ClientMonitoringPromptCoverageRequestDto(), { project: projectContext?.project, deliberateBlanks: next })
      );
      setDeliberateBlanks(next);
      setActionError(undefined);
    } catch (blankError) {
      setActionError((blankError as Error)?.message || 'Unable to record this as left blank on purpose.');
    } finally {
      setSavingBlank(false);
    }
  };

  /**
   * Applies a change to one prompt of the working set.
   *
   * A refusal the server sent names prompts by their place in the list it was given, so any change
   * to that list makes it describe something else; it is dropped here rather than left pointing at a
   * prompt nobody objected to.
   */
  const adjustPrompt = (index: number, changes: Partial<ClientRecommendedMonitoringPromptDto>) => {
    if (confirmed) {
      return;
    }

    setRejections(undefined);
    setPrompts((current) => current.map((entry, position) => (position === index ? promptWith(entry, changes) : entry)));
  };

  const removePrompt = (index: number) => {
    if (confirmed) {
      return;
    }

    // Every row below this one moves up, so the typed cadences and the server's refusal are dropped
    // rather than re-attached to the wrong prompt; each field falls back to its own prompt's value.
    setFrequencyText({});
    setRejections(undefined);
    setPrompts((current) => current.filter((_, position) => position !== index));
  };

  const goToReview = () => {
    if (!taskGroupId) {
      return;
    }

    const reviewPath = brandPresenceOnboardingStepPath(BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS.stepReview, taskGroupId);
    sendToParent({ type: 'update-path', path: reviewPath });
    navigate(reviewPath, { replace: true });
  };

  const goToLocaleSettings = () => {
    const localePath = taxonomyPath(TaxonomyFacet.Locale);
    sendToParent({ type: 'update-path', path: localePath });
    navigate(localePath);
  };

  const confirmSet = async () => {
    if (!api || !projectContext?.project || lacksCountryLanguage) {
      return;
    }

    setConfirming(true);
    setRejections(undefined);
    setConfirmWarnings(undefined);
    setActionError(undefined);

    try {
      const result = await api.governance.monitoringPrompts.recommendationConfirm(
        Object.assign(new ClientMonitoringPromptRecommendationConfirmDto(), { project: projectContext?.project, prompts })
      );

      const warnings = result.warnings || [];
      setConfirmWarnings(warnings);
      setConfirmed(true);

      // A set that was written cleanly moves on by itself. One the server had something to say about
      // stops here with what it said, so the reader leaves having seen it rather than finding it gone.
      if (!warnings.length) {
        goToReview();
      }
    } catch (confirmError) {
      const named = confirmRejectionsOf(confirmError);
      if (named?.length) {
        setRejections(named);
      } else {
        setActionError(refusalSentenceOf(confirmError) ?? ((confirmError as Error)?.message || 'Unable to create the prompts.'));
      }
    } finally {
      setConfirming(false);
    }
  };

  const goBack = () => {
    if (!taskGroupId) {
      return;
    }

    const backPath = brandPresenceOnboardingStepPath(BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS.stepTaxonomyTermsStrategic, taskGroupId);
    sendToParent({ type: 'update-path', path: backPath });
    navigate(backPath, { replace: true });
  };

  const tagOptionsByKey = React.useMemo(
    () =>
      new Map<string, IRuntimeRevisionReference<ClientTaxonomyTermEntity>>(
        (tagOptions || []).map((term) => [term.key, new StaticRevisionReference(term)])
      ),
    [tagOptions]
  );

  return (
    <GovernanceOnboardingContainer>
      <GovernanceOnboardingStepContainer
        step={PROMPT_STEP_NUMBER}
        totalSteps={BRAND_PRESENCE_ONBOARDING_TOTAL_STEPS}
        title="Start monitoring with a recommended set"
        description="Nothing is created until you confirm. Review what we suggest, adjust anything, then create the set."
        onBack={goBack}
        onContinue={() => {
          if (confirmed) {
            goToReview();
            return;
          }

          void confirmSet();
        }}
        continueLabel={confirmed ? 'Continue' : confirming ? 'Creating…' : 'Create these prompts'}
        continueDisabled={loading || confirming || (!confirmed && (!weightingConfirmed || prompts.length === 0 || lacksCountryLanguage))}
      >
        <div className="w-full space-y-8">
          {/* Creating is not something to do twice, so once a set exists the step says so above every
              control it has closed, rather than below them. */}
          {confirmed ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              These prompts are set up. They can be changed from the monitoring settings once onboarding is finished.
            </div>
          ) : null}

          <div className="space-y-2">
            <span className="text-sm font-semibold text-slate-900">How much to start with</span>
            <div className="flex flex-wrap gap-2">
              {TIERS_IN_ORDER.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={option === tier}
                  disabled={confirmed}
                  onClick={() => setTier(option)}
                  className={
                    option === tier
                      ? 'rounded-lg border border-primary bg-primary/10 px-4 py-2 text-sm font-semibold text-primary disabled:cursor-not-allowed disabled:opacity-50'
                      : 'rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-slate-200'
                  }
                >
                  {translateEnumValue('MonitoringPromptRecommendationTier', option)}
                </button>
              ))}
            </div>
          </div>

          {loading ? <GovernanceOnboardingListSkeleton label="Building your recommended prompts…" /> : null}

          {loadError ? (
            <div className="space-y-2">
              <div className="text-sm text-danger">{loadError}</div>
              <button
                type="button"
                className="text-sm font-semibold text-primary hover:underline"
                onClick={() => setRetry((count) => count + 1)}
              >
                Try again
              </button>
            </div>
          ) : null}

          {/* An action that did not land leaves what is on screen alone: asking for the set again
              would throw away the reader's own changes, so nothing here offers to. */}
          {actionError ? <div className="text-sm text-danger">{actionError}</div> : null}

          {!loading && !loadError && recommendation && prompts.length === 0 && !confirmed ? (
            <div className="text-sm text-slate-500">
              There is nothing left to create. Choose a different amount above, or go back and add more tags first.
            </div>
          ) : null}

          {recommendation && !loading ? (
            <>
              {/* Two different numbers: how many prompts are in the set the reader is looking at,
                    and the arithmetic the recommender did for the set it proposed — one templated
                    prompt becomes as many generated prompts as it has tags, so the two never match
                    by construction. The arithmetic describes what was proposed and is not recomputed. */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                {`${prompts.length} ${prompts.length === 1 ? 'prompt' : 'prompts'} in this set. As proposed, that is ` +
                  `${recommendation.sizing?.instances ?? 0} generated prompts, asked ${recommendation.sizing?.runsPerDay ?? 0} times a day.`}
              </div>

              {lacksCountryLanguage && !confirmed ? (
                <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                  <p>Add at least one country and language to create these prompts.</p>
                  <button type="button" className="font-semibold text-primary hover:underline" onClick={goToLocaleSettings}>
                    Add a country and language
                  </button>
                </div>
              ) : null}

              {recommendation.warnings?.length ? (
                <div className="space-y-1">
                  <span className="text-sm font-semibold text-slate-900">Worth knowing</span>
                  <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
                    {recommendation.warnings.map((warning, position) => (
                      <li key={`preview-warning-${position}`}>{warning}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="space-y-3">
                <span className="text-sm font-semibold text-slate-900">What this set covers</span>
                <CoverageRow label="Attributes" coverage={recommendation.coverage?.attributes} api={api} />
                <CoverageRow label="Angles" coverage={recommendation.coverage?.angles} api={api} />
                <CoverageRow label="Industries" coverage={recommendation.coverage?.sectors} api={api} />
              </div>

              <div className="space-y-2">
                <span className="text-sm font-semibold text-slate-900">Topics by question kind</span>
                {/* Wider than a phone's screen, the table scrolls sideways in its own box. */}
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr>
                        <th className="pb-2 pr-3 font-semibold text-slate-900">Topic</th>
                        {RECOMMENDER_ASSIGNABLE_PURPOSE_TYPES.map((purposeType) => (
                          <th key={purposeType} className="pb-2 pr-3 font-semibold text-slate-900">
                            {translateEnumValue('MonitoringPromptPurposeType', purposeType)}
                          </th>
                        ))}
                        <th className="pb-2 font-semibold text-slate-900">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topicReferences.map((topic) => {
                        const topicKey = topic.getStableKey();
                        const topicTotal = RECOMMENDER_ASSIGNABLE_PURPOSE_TYPES.reduce(
                          (total, purposeType) => total + (countsByCell.get(matrixCellKey(topicKey, purposeType)) ?? 0),
                          0
                        );

                        return (
                          <tr key={topicKey} className="border-t border-slate-100">
                            <th scope="row" className="py-2 pr-3 font-normal">
                              <TaxonomyTermNameBadge api={api} termKey={topicKey} applyFacetPresentation />
                            </th>
                            {RECOMMENDER_ASSIGNABLE_PURPOSE_TYPES.map((purposeType) => {
                              const cellKey = matrixCellKey(topicKey, purposeType);
                              const count = countsByCell.get(cellKey) ?? 0;
                              const purposeLabel = translateEnumValue('MonitoringPromptPurposeType', purposeType);

                              if (count > 0) {
                                return (
                                  <td key={purposeType} className="py-2 pr-3 tabular-nums text-slate-700">
                                    {count}
                                  </td>
                                );
                              }

                              if (blankCellKeys.has(cellKey)) {
                                return (
                                  <td key={purposeType} className="py-2 pr-3 text-xs text-slate-500">
                                    Left blank on purpose
                                  </td>
                                );
                              }

                              return (
                                <td key={purposeType} className="py-2 pr-3 text-xs">
                                  <span className="mr-2 font-semibold text-warning">Nothing asked</span>
                                  <button
                                    type="button"
                                    disabled={confirmed || savingBlank || !projectContext?.project}
                                    onClick={() => {
                                      void markDeliberateBlank(topic, purposeType);
                                    }}
                                    className="font-semibold text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                                    aria-label={`Leave ${purposeLabel} blank on purpose for this topic`}
                                  >
                                    Leave blank on purpose
                                  </button>
                                </td>
                              );
                            })}
                            <td className="py-2 tabular-nums text-slate-700">{topicTotal}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-2">
                <span className="text-sm font-semibold text-slate-900">How the audiences are weighted</span>
                {recommendation.personaWeighting?.length ? (
                  <ul className="space-y-1 text-sm text-slate-700">
                    {recommendation.personaWeighting.map((weighting) => (
                      <li key={weighting.persona.getStableKey()} className="flex flex-wrap items-center gap-2">
                        <TaxonomyTermNameBadge api={api} termKey={weighting.persona.getStableKey()} applyFacetPresentation />
                        {weighting.quotas.map((quota) => (
                          <span key={quota.stage.getStableKey()} className="flex items-center gap-1 text-xs text-slate-600">
                            <TaxonomyTermNameBadge api={api} termKey={quota.stage.getStableKey()} applyFacetPresentation />
                            <span className="tabular-nums">{quota.count}</span>
                          </span>
                        ))}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-500">No audience weighting was applied to this set.</p>
                )}
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    disabled={confirmed}
                    checked={weightingConfirmed}
                    onChange={(event) => setWeightingConfirmed(event.target.checked)}
                  />
                  This weighting is right for us
                </label>
              </div>

              {recommendation.excludedEngines?.length ? (
                <div className="space-y-1">
                  <span className="text-sm font-semibold text-slate-900">Left out of the daily count</span>
                  <ul className="space-y-1 text-sm text-slate-700">
                    {recommendation.excludedEngines.map((excluded) => (
                      <li key={excluded.engine.getStableKey()} className="flex flex-wrap items-center gap-2">
                        <TaxonomyTermNameBadge api={api} termKey={excluded.engine.getStableKey()} applyFacetPresentation />
                        <span className="text-xs text-slate-600">{excluded.reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {confirmWarnings?.length ? (
                <div className="space-y-1">
                  <span className="text-sm font-semibold text-slate-900">After creating</span>
                  <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
                    {confirmWarnings.map((warning, position) => (
                      <li key={`confirm-warning-${position}`}>{warning}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {rejections?.length ? (
                <div className="text-sm text-danger">Nothing was created. Fix the prompts marked below and try again.</div>
              ) : null}

              <div className="space-y-4">
                {prompts.map((prompt, index) => {
                  const rejection = rejections?.find((entry) => entry.promptIndex === index);
                  const extraTags = prompt.extractionExtraTerms || [];
                  const extraTagsByKey = new Map(extraTags.map((reference) => [reference.getStableKey(), reference]));
                  const selectedTagValues = extraTags.map((reference) => reference.getStableKey());
                  const offeredKeys = new Set((tagOptions || []).map((term) => term.key));
                  // A tag the prompt already carries is listed too, even of a vocabulary no control
                  // offers, so it renders as a chip the reader can see and remove; being listed and
                  // being selected keeps it out of what the dropdown offers to add.
                  const tagControlOptions = [
                    ...(tagOptions || []).map((term) => ({ value: term.key, label: term.name })),
                    ...selectedTagValues
                      .filter((key) => !offeredKeys.has(key))
                      .map((key) => ({ value: key, label: termIndex?.byKey.get(key)?.name ?? key })),
                  ];

                  return (
                    <div
                      key={`prompt-${index}`}
                      className={
                        rejection
                          ? 'space-y-3 rounded-lg border border-danger bg-white p-4'
                          : 'space-y-3 rounded-lg border border-slate-200 bg-white p-4'
                      }
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900">{prompt.displayName}</div>
                          <p className="text-sm text-slate-600">{prompt.promptText}</p>
                        </div>
                        <button
                          type="button"
                          disabled={confirmed}
                          onClick={() => removePrompt(index)}
                          className="shrink-0 text-sm font-semibold text-slate-500 hover:text-danger disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-slate-500"
                          aria-label={`Remove ${prompt.displayName}`}
                        >
                          Remove
                        </button>
                      </div>

                      {rejection ? <div className="text-sm text-danger">{`Prompt ${index + 1}: ${rejection.reason}`}</div> : null}

                      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                        {prompt.persona ? (
                          <TaxonomyTermNameBadge api={api} termKey={prompt.persona.getStableKey()} applyFacetPresentation />
                        ) : null}
                        {prompt.funnelStage ? (
                          <TaxonomyTermNameBadge api={api} termKey={prompt.funnelStage.getStableKey()} applyFacetPresentation />
                        ) : null}
                        {prompt.sector ? (
                          <TaxonomyTermNameBadge api={api} termKey={prompt.sector.getStableKey()} applyFacetPresentation />
                        ) : null}
                        {prompt.topics?.map((topic) => (
                          <TaxonomyTermNameBadge
                            key={topic.getStableKey()}
                            api={api}
                            termKey={topic.getStableKey()}
                            applyFacetPresentation
                          />
                        ))}
                      </div>

                      {prompt.templateFacet ? (
                        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                          <span>{`One per ${translateEnumValue('TaxonomyFacet', prompt.templateFacet)}:`}</span>
                          {prompt.templateTerms?.map((term) => (
                            <TaxonomyTermNameBadge
                              key={term.getStableKey()}
                              api={api}
                              termKey={term.getStableKey()}
                              applyFacetPresentation
                            />
                          ))}
                        </div>
                      ) : null}

                      {prompt.countryLocales?.length ? (
                        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                          <span>Asked in:</span>
                          {prompt.countryLocales.map((pair) => (
                            <span key={`${pair.country}:${pair.locale.getStableKey()}`} className="flex items-center gap-1">
                              <span>{translateEnumValue('MonitoringPromptCountry', pair.country)}</span>
                              <TaxonomyTermNameBadge api={api} termKey={pair.locale.getStableKey()} />
                            </span>
                          ))}
                        </div>
                      ) : null}

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        {/* Shown, not chosen: Sync Core reads the kind from the prompt's own text and
                            refuses a kind of another family, so a choice here could only be refused. */}
                        <div className="text-sm text-slate-700">
                          <span className="mb-1 block font-semibold text-slate-900">What it asks for</span>
                          <span className="flex h-10 items-center">
                            {translateEnumValue('MonitoringPromptPurposeType', prompt.purposeType)}
                          </span>
                        </div>

                        <label className="text-sm text-slate-700">
                          <span className="mb-1 block font-semibold text-slate-900">How often, in hours</span>
                          <input
                            type="number"
                            min={1}
                            disabled={confirmed}
                            // The field shows what was typed, so it can be emptied and retyped; the
                            // set keeps the last whole number it held, because a cadence of nothing
                            // is not a cadence the confirm could send.
                            value={frequencyText[index] ?? String(prompt.frequencyHours)}
                            onChange={(event) => {
                              const typed = event.target.value;
                              setFrequencyText((current) => ({ ...current, [index]: typed }));

                              const hours = Number(typed);
                              if (!typed.trim() || !Number.isInteger(hours) || hours <= 0) {
                                return;
                              }

                              adjustPrompt(index, { frequencyHours: hours });
                            }}
                            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700"
                          />
                          <span className="mt-1 block text-xs text-slate-500">{prompt.frequencyNote}</span>
                        </label>
                      </div>

                      <div>
                        {tagOptions ? (
                          <ChipsDropdownSelect
                            label="Extra tags to read from the answers"
                            values={selectedTagValues}
                            options={tagControlOptions}
                            addLabel="Add tag"
                            onChange={(next) => {
                              // Rebuilt from the tags this prompt already carries FIRST, so a tag whose
                              // option row is missing keeps the reference the preview handed over
                              // instead of being dropped on the next adjustment.
                              const references = next
                                .map((key) => extraTagsByKey.get(key) ?? tagOptionsByKey.get(key))
                                .filter((reference): reference is IRuntimeRevisionReference<ClientTaxonomyTermEntity> => !!reference);

                              adjustPrompt(index, { extractionExtraTerms: references });
                            }}
                          />
                        ) : (
                          <div className="text-sm text-slate-500">Loading the tags you can add…</div>
                        )}
                        {extractionHintNames.length ? (
                          <p className="mt-1 text-xs text-slate-500">
                            {formatFacetNameList(extractionHintNames)} are read from every answer automatically, so you do not need to add
                            them here.
                          </p>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
      </GovernanceOnboardingStepContainer>
    </GovernanceOnboardingContainer>
  );
}
